import { Component, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { ChartModule } from 'primeng/chart';
import Chart from 'chart.js/auto';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { combineLatest } from 'rxjs';
import { TranslocoService } from '@jsverse/transloco';
import { DecimalPipe } from '@angular/common';

@Component({
  selector: 'app-cost-analysis-chart',
  standalone: true,
  imports: [ChartModule],
  providers: [DecimalPipe],
  templateUrl: './cost-analysis-chart.component.html',
  styleUrl: './cost-analysis-chart.component.css'
})
export class CostAnalysisChartComponent implements OnInit, OnChanges {
  data: any;
  options: any;

  // Dummy data
  @Input() showDummyData: boolean = false;
  dummylineCostTrendData: number[] = [120, 123.28];
  dummybarkWhData: number[] = [12.182, 13.182];
  dummykwhDifference: number = 0;
  dummycostDifference: number = 0;

  periodLabels: string[] = ['Previous Period', 'Current Period'];

  labelCostTrend: string = "Total Costs"

  @Input() lineCostTrendData: number[] = [120, 123.28];
  @Input() barkWhData: number[] = [12.182, 13.182];
  @Input() inputedPeriods: string[] | undefined = undefined;
  @Input() kwhDifference: number = 1;
  @Input() costDifference: number = 3.28;

  constructor(private translocoService: TranslocoService, private decimalPipe: DecimalPipe) {
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (this.inputedPeriods === undefined) {
      const monthKeys = [
        'dashboard.period.previous',
        'dashboard.period.current',
      ];

      combineLatest(monthKeys.map((key) => this.translocoService.selectTranslate(key))).subscribe((translations) => {
        this.periodLabels = translations;
        this.updateChart();
      });
    }
    else {
      this.periodLabels = this.inputedPeriods;
    }
    this.updateChart();
  }

  ngOnInit(): void {
    this.updateChart();

    this.translocoService.selectTranslate('dashboard.total_costs_label').subscribe((translation) => {
      this.labelCostTrend = translation;
    });

    const monthKeys = [
      'dashboard.period.previous',
      'dashboard.period.current',
    ];

    if (this.inputedPeriods === undefined) {
      combineLatest(monthKeys.map((key) => this.translocoService.selectTranslate(key))).subscribe((translations) => {
        this.periodLabels = translations;
        this.updateChart();
      });
    }
    else {
      this.periodLabels = this.inputedPeriods;
    }
  }

  updateChart(): void {
    if (this.showDummyData) {
      this.lineCostTrendData = this.dummylineCostTrendData;
      this.barkWhData = this.dummybarkWhData;
      this.kwhDifference = this.dummykwhDifference;
      this.costDifference = this.dummycostDifference;
    }

    Chart.register(ChartDataLabels);

    this.data = {
      labels: this.periodLabels,
      datasets: [
        {
          type: 'line',
          label: this.labelCostTrend,
          data: this.lineCostTrendData,
          borderColor: '#AAC3C2',
          borderWidth: 2,
          tension: 0.4,
          pointStyle: 'circle',
          pointRadius: 6,
          pointHoverRadius: 8,
          pointBackgroundColor: '#EEB269',
          datalabels: {
            display: 'auto',
            clamp: true,
            clip: true,
            align: 'start',
            anchor: 'start',
            color: '#000',
            font: {
              size: 10,
              weight: 'bold',
              family: 'Gilroy',
            },
            formatter: (value: number, context: any) => {
              if (context.dataIndex === 1) {
                const kwhSign = this.kwhDifference >= 0 ? '+' : '-';
                const costSign = this.costDifference >= 0 ? '+' : '-';

                const kwhDifference = Math.abs(this.kwhDifference);
                const costDifference = Math.abs(this.costDifference);

                return `${kwhSign} ${this.decimalPipe.transform(kwhDifference, '1.0-0') || kwhDifference.toFixed(2)} kWh\n${costSign} ${this.decimalPipe.transform(costDifference, '1.0-0') || costDifference.toFixed(2)}€`;
              }
              return '';
            }
          },
          xAxisID: 'x2',
          yAxisID: 'y2',
        },
        {
          type: 'bar',
          label: 'kWh',
          data: this.barkWhData,
          backgroundColor: ['#D2DCC8', '#A4BD8E'],
          borderRadius: 12,
          datalabels: {
            display: 'auto',
            clamp: true,
            clip: true,
            align: 'center',
            anchor: 'center',
            color: '#000',
            font: {
              size: 10,
              weight: 'normal',
              family: 'Gilroy',
            },
            formatter: (value: number) => `${this.decimalPipe.transform(value, '1.0-0') || value} kWh`,
          },
        },
      ],
    };

    this.options = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          display: false,
        },
        tooltip: {
          callbacks: {
            label: (context: any) => {
              const index = context.dataIndex;
              let label = context.dataset.label || '';
              let value = context.raw || 0;
              return `${label}: ${this.decimalPipe.transform(value, '1.0-0') || value}`;
            }
          }
        },
      },
      scales: {
        x: {
          stacked: false,
          display: false,
        },
        y: {
          beginAtZero: true,
          grid:
          {
            display: true,
          },
          border: {
            dash: [5, 5],
          },
          ticks: {
            display: false,
          },
        },
        x2: {
          beginAtZero: false,
          display: false,
        },
        y2: {
          beginAtZero: true,
          display: false,
        }
      },
    };
  }
}
